import React, { useState } from "react";
import { Link } from "react-router-dom";

import Header from "components/Header";
import TextBox from "components/TextBox";
import { AppRoutes } from "pages/constants/routes";
import { emailSchema } from "validation/schemas/email";

import styles from "./ResetPasswordRequest.module.scss";
import AuthService from "api/Auth/AuthService";
import FinishDialog from "components/FinishDialog";

const ResetPasswordRequest = () => {
  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string>();
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const [isSuccessDialogOpen, setIsSuccessDialogOpen] =
    useState<boolean>(false);

  const validateEmail = (email: string): boolean => {
    try {
      const result = emailSchema.safeParse({ email: email.trim() });
      if (!result.success) {
        setEmailError(result.error.errors[0].message);
        return false;
      }
      return true;
    } catch (error) {
      setEmailError("Failed to verify email. Please try again.");
      return false;
    }
  };

  const onChangeWrapper =
    (
      setValue: (newValue: string) => void,
      setError: (newValue?: string) => void,
    ) =>
    (newValue: string) => {
      setValue(newValue);
      setError(undefined);
    };

  const onEmailSubmit = async () => {
    try {
      setIsSubmitting(true);
      setEmailError(undefined);

      // Validate email before proceeding
      const isValid = await validateEmail(email);
      if (!isValid) {
        return;
      }

      // Proceed with password reset request
      await AuthService.resetPasswordRequest(
        email,
        window.origin + AppRoutes.resetPassword,
      );
      setIsSuccessDialogOpen(true);
    } catch (err) {
      // TODO: Show error properly
      setEmailError("An error occured, please try again...");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.background} />
      <Header hideSignIn />
      <div className={styles.content}>
        <div className={styles.container}>
          <div className={styles.header}>
            <div className={styles.title}>Forget Your Password?</div>
            <div className={styles.subtitle}>
              We just need your email to send you a reset link.
            </div>
          </div>
          <div className={styles.inputContainer}>
            <TextBox
              lean
              error={emailError !== undefined}
              placeholder="Email"
              value={email}
              onChange={onChangeWrapper(setEmail, setEmailError)}
              onSubmit={isSubmitting ? undefined : onEmailSubmit}
            />
            {emailError && (
              <div className={styles.fieldError}>{emailError}</div>
            )}
          </div>
          <div className={styles.signInContainer}>
            <span className={styles.text}>Return to</span>
            <Link to={AppRoutes.signin}>sign in</Link>
            <span className={styles.text}>•</span>
            <Link to={AppRoutes.signup}>sign up</Link>
          </div>
        </div>
      </div>

      {/* Success Dialog for successful ticket creation */}
      <FinishDialog
        isOpen={isSuccessDialogOpen}
        setIsOpen={setIsSuccessDialogOpen}
        title="Success!"
        message="If your email is registered with us, you will receive an email in your inbox"
      />
    </div>
  );
};

export default ResetPasswordRequest;
